/**
 * Determines the color classes based on the category.
 *
 * @param {string} category - The category to determine the color classes for.
 * @return {string} The color classes corresponding to the category.
 */
const getCircleColor = (category) => {
  switch (category) {
    case "Good":
      return "text-green-500 border-green-500";
    case "Needs Improvement":
      return "text-yellow-500 border-yellow-500";
    case "Poor":
      return "text-red-500 border-red-500";
    default:
      return "text-muted-foreground border-input";
  }
};

/**
 * Determines the performance category based on the score.
 *
 * @param {number} score - The score to determine the category for.
 * @return {string} The performance category corresponding to the score.
 */
const getPerformanceCategory = (score) => {
  if (score >= 90) return "Good";
  if (score >= 50) return "Needs Improvement";
  return "Poor";
};

/**
 * Extracts the Google Sheet ID from a Google Sheets URL.
 *
 * @param {string} url - The Google Sheets URL.
 * @return {string|null} The extracted Sheet ID or null if the URL is invalid.
 */
const extractSheetIdFromUrl = (url) => {
  try {
    // Check if the URL is a valid Google Sheets URL
    if (!url.includes("docs.google.com/spreadsheets/d/")) {
      return null; // Not a Google Sheets URL
    }

    // Extract the Sheet ID using a regex
    const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : null; // Return the Sheet ID or null if no match
  } catch (error) {
    console.error("Error extracting Sheet ID from URL:", error);
    return null; // Return null if an error occurs
  }
};

/**
 * Normalizes URL input for testing. Accepts:
 * - Full URLs: https://example.com, http://example.com
 * - Domains: example.com, seelay.in
 * - Subdomains: en.jt3.japantravel.com
 * Adds https:// if no protocol is present.
 *
 * @param {string} input - URL or domain string.
 * @returns {string} Normalized URL with protocol.
 */
const normalizeUrl = (input) => {
  if (!input || typeof input !== "string") return "";
  const trimmed = input.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return "https://" + trimmed;
};

/**
 * Extracts domain/hostname from URL or domain string.
 * Handles: https://example.com, example.com, en.jt3.japantravel.com
 *
 * @param {string} input - URL or domain string.
 * @returns {string|null} Hostname without www. or null if invalid.
 */
const extractDomain = (input) => {
  if (!input || typeof input !== "string" || !input.trim()) return null;
  const normalized = normalizeUrl(input.trim());
  try {
    const hostname = new URL(normalized).hostname;
    return hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
};

/**
 * Returns a fallback avatar URL using first and last name initials.
 * Uses ui-avatars.com - e.g. "Aman Kumar" → "AK", "John Doe" → "JD".
 *
 * @param {string} nameOrEmail - Full name (e.g. "Aman Kumar") or email for fallback.
 * @returns {string} URL for initials-based avatar.
 */
const getAvatarFallbackUrl = (nameOrEmail) => {
  const displayName = nameOrEmail?.trim() || "User";
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}`;
};

const regionNames =
  typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const formatCountryName = (code) => {
  if (!code) return "–";
  const normalized = String(code).trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return code;
  try {
    return regionNames?.of(normalized) || code;
  } catch {
    return code;
  }
};

export {
  getCircleColor,
  getPerformanceCategory,
  extractSheetIdFromUrl,
  getAvatarFallbackUrl,
  normalizeUrl,
  extractDomain,
  formatCountryName,
};
